import { useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  DEFAULT_CANCELLATION_DEADLINE_HOURS,
  HOLIDAYS,
  holidayName,
  localDateOf,
  resolveCancellationDeadlineHours,
} from '../../engine';
import { toMinHHMM } from '../../lib/date';
import { useT } from '../../i18n';
import { OptionPicker } from './OptionPicker';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import {
  DURATION_BOUNDS,
  GAP_BOUNDS,
  WINDOW_DAY_BOUNDS,
  removeCustomOption,
  selectionAfterRemoval,
  valueAfterRemoval,
} from './customOptions';

// The product's own chips. The club's additions live in the booking config
// (`customGapOptions` and friends) and are merged in by OptionPicker.
//
// TRIMMED once custom values existed: the rows had grown into a wall of
// numbers, most of which no club used, and a long row of near-identical chips
// is harder to read than a short one plus a field. 45/75/105/150/240 minutes,
// 60/90 days, and 45 minutes from the gap row came out.
//
// REMOVING A BUILT-IN DOES NOT CHANGE ANY CLUB'S SETTING — nothing rewrites
// stored documents, and it must not. A club left on one of these keeps it, and
// OptionPicker still renders it because `mergedOptions` is given what is in
// force; it simply has no chip of its own to come back to once changed.
const GAP_BUILT_IN = [0, 30, 60, 90];
const DURATION_BUILT_IN = [30, 60, 90, 120, 180];
const WINDOW_BUILT_IN = [3, 7, 14, 30];
/**
 * The cancellation deadlines offered, in hours before start (#1135). A fixed
 * row: zero is «until the booking starts», and a week is the most the engine
 * accepts (`MAX_CANCELLATION_DEADLINE_HOURS`).
 */
const CANCELLATION_HOURS = [0, 1, 2, 4, 6, 12, 24, 48];

/**
 * Has anything in the draft actually moved?
 *
 * `JSON.stringify` on both sides would be shorter and wrong: it compares KEY
 * ORDER as well as content, and `courtMeta` gains and loses keys as courts are
 * customised. A court renamed and renamed back would come out «changed»,
 * which is the exact thing this is meant to stop reporting.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => sameValue(v, b[i]));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a as object);
    const kb = Object.keys(b as object);
    if (ka.length !== kb.length) return false;
    return ka.every(
      (k) => Object.prototype.hasOwnProperty.call(b, k) && sameValue((a as any)[k], (b as any)[k]),
    );
  }
  return false;
}

export interface BookingConfigEditorProps {
  /** Current booking config (each field is read defensively with a fallback). */
  config: any;
  /** Physical number of courts the center has registered (clubInfo.courts). */
  totalCourts?: number;
  /** Closes the modal without saving. */
  onClose: () => void;
  /** Persists the edited config (the transient `_addHoliday` field is stripped first). */
  onSave: (config: any) => void;
  /**
   * Has the club SAVED its opening hours (`hasSavedBookingHours` on the stored
   * document, #1192)? `false` means booking is closed until a save, so the
   * dialog is saveable as it opens (#1299). `null`/absent is «unknown» — a
   * failed read claims nothing, and Save keeps its dead-until-dirty rule.
   */
  hoursSaved?: boolean | null;
}

/** Booking-rules editor modal (gap rule, durations, opening hours, holidays, courts); extracted verbatim from the monolith (legacy/App.jsx). */
export function BookingConfigEditor({
  config,
  totalCourts = 4,
  onClose,
  onSave,
  hoursSaved = null,
}: BookingConfigEditorProps) {
  const t = useT();
  const uid = safeId('booking-config', useId());
  const ids = {
    title: `${uid}-title`,
    lead: `${uid}-lead`,
    gap: `${uid}-gap`,
    durations: `${uid}-durations`,
    hours: `${uid}-hours`,
    openTime: `${uid}-open-time`,
    closeTime: `${uid}-close-time`,
    availability: `${uid}-availability`,
    availabilityFrom: `${uid}-availability-from`,
    availabilityTo: `${uid}-availability-to`,
    window: `${uid}-window`,
    cancellation: `${uid}-cancellation`,
    holidays: `${uid}-holidays`,
    holidayCountry: `${uid}-holiday-country`,
    addHoliday: `${uid}-add-holiday`,
    courtCount: `${uid}-court-count`,
    facilities: `${uid}-facilities`,
    activeCourts: `${uid}-active-courts`,
  };
  // useState<any>: the draft carries a transient `_addHoliday` field that is
  // added dynamically (and stripped on save). Under the monolith's @ts-nocheck
  // this needed no annotation; in typed src/ we widen to `any` so tsc accepts
  // the extra key without changing any runtime behaviour.
  //
  // A FUNCTION, so the pristine copy below is built by the same code. Two
  // hand-kept literals would drift, and the one that drifted would make the
  // dialog open already claiming unsaved changes.
  const buildDraft = () => ({
    bufferMin: config.bufferMin ?? 60,
    allowedDurations: [...(config.allowedDurations || [60, 90, 120])],
    openTime: config.openTime || '07:00',
    closeTime: config.closeTime || '23:00',
    enabledCourts: [...(config.enabledCourts || [])], // empty = all
    stepMin: config.stepMin || 30,
    smartAllocation: config.smartAllocation !== false,
    allowDirectBooking: config.allowDirectBooking !== false,
    availabilityFromTime: config.availabilityFromTime ?? null,
    availabilityToTime: config.availabilityToTime ?? null,
    bookingWindowDays: config.bookingWindowDays ?? 7,
    // Through the resolver, so the dialog shows what is IN FORCE — a stored
    // value the engine reads as the default shows as the default (#1135).
    cancellationDeadlineHours: resolveCancellationDeadlineHours(config),
    // The club's own chips for the three pickers. MENU DATA, not rules: the
    // engine reads bufferMin / allowedDurations / bookingWindowDays exactly as
    // before and never looks at these lists.
    customGapOptions: [...(config.customGapOptions || [])],
    customDurationOptions: [...(config.customDurationOptions || [])],
    customWindowDayOptions: [...(config.customWindowDayOptions || [])],
    courtCount: config.courtCount ?? totalCourts,
    courtMeta: { ...(config.courtMeta || {}) },
    courtDefaults: {
      ...(config.courtDefaults || { courtType: 'double', runoff: false, streaming: false }),
    },
    holidayCountry: config.holidayCountry || 'no',
    holidayExtra: [...(config.holidayExtra || [])],
    holidayRemoved: [...(config.holidayRemoved || [])],
  });

  const [draft, setDraft] = useState<any>(buildDraft);

  // WHAT THE DIALOG OPENED ON, so Save can stay dead until something moves.
  //
  // Compared against the CONFIG rather than the draft would be wrong: the draft
  // fills in defaults the stored document may not carry, so a club that has
  // never set `stepMin` would look edited the moment the dialog opened.
  //
  // A lazy ref, not `useMemo`: this must be captured once for the life of the
  // dialog. `useMemo` is a performance hint the runtime may discard, and a
  // recomputed baseline would quietly adopt the edits as the starting point.
  const pristine = useRef<any>(null);
  if (pristine.current === null) pristine.current = buildDraft();

  // `_addHoliday` is the transient date in the «add a holiday» field. Typing
  // there is not an edit to the settings — the edit is pressing Add, which
  // lands in holidayExtra — so it is stripped here exactly as it is on save.
  const { _addHoliday: _typing, ...draftClean } = draft;
  //
  // UNSAVED HOURS ARE A CHANGE IN THEMSELVES (#1299). The draft shows the
  // engine's 07:00–23:00 when the stored document has none, and the baseline is
  // built the same way, so a club happy with those defaults would see a dead
  // Save and could never open booking. `config` is the MERGED document (the
  // defaults are already in it), which is why this is told by the caller from
  // the STORED one rather than read off `config` here.
  const hoursUnsaved = hoursSaved === false;
  const dirty = hoursUnsaved || !sameValue(draftClean, pristine.current);

  // Court name/sponsor are hidden by default — only shown if the admin wants to
  // customize, or if metadata already exists from before.
  const [customizeCourts, setCustomizeCourts] = useState(
    () => Object.keys(config.courtMeta || {}).length > 0,
  );

  // Settings tabs: 'general' (rules/time/holidays) and 'courts' (courts).
  const [activeTab, setActiveTab] = useState('general');

  // Weekday shorthands for DISPLAY only — the ISO date is the stored value
  // (docs/architecture/DOMAIN_RULES.md §4), never this label.
  const weekdayShort = [
    t('booking.weekday.short.sun'),
    t('booking.weekday.short.mon'),
    t('booking.weekday.short.tue'),
    t('booking.weekday.short.wed'),
    t('booking.weekday.short.thu'),
    t('booking.weekday.short.fri'),
    t('booking.weekday.short.sat'),
  ];

  const toggleDuration = (d) => {
    setDraft((s) => ({
      ...s,
      allowedDurations: s.allowedDurations.includes(d)
        ? s.allowedDurations.filter((x) => x !== d).sort((a, b) => a - b)
        : [...s.allowedDurations, d].sort((a, b) => a - b),
    }));
  };
  const toggleCourt = (c) => {
    setDraft((s) => {
      const list = s.enabledCourts;
      // If the list is empty (= all active), then checking a box means we want to
      // DE-activate that court. Build a list of all the others.
      if (list.length === 0) {
        const all = Array.from({ length: totalCourts }, (_, i) => i + 1);
        return { ...s, enabledCourts: all.filter((x) => x !== c) };
      }
      return {
        ...s,
        enabledCourts: list.includes(c)
          ? list.filter((x) => x !== c)
          : [...list, c].sort((a, b) => a - b),
      };
    });
  };
  const isCourtEnabled = (c) => draft.enabledCourts.length === 0 || draft.enabledCourts.includes(c);

  // A `Dialog` (#900): named by its own title, focus moved in, kept inside and
  // given back to the opener, Escape closes as the backdrop does. The inline
  // styles are the hand-built overlay's, moved verbatim.
  return (
    <Dialog
      labelledBy={ids.title}
      describedBy={ids.lead}
      onClose={onClose}
      showClose={false}
      backdropClassName=""
      backdropStyle={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 22, 17, 0.55)',
      }}
      className=""
      style={{
        background: '#ffffff',
        maxWidth: 640,
        width: '100%',
        maxHeight: 'calc(100dvh - 100px)',
        marginTop: 80,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
        border: '1px solid #e7eae3',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid #e7eae3',
          background: '#fafbf7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div>
          <h3 id={ids.title} className="font-display text-lg font-semibold leading-tight">
            {t('booking.config.title')}
          </h3>
          <p id={ids.lead} className="text-[11px] text-ink-3 mt-0.5">
            {t('booking.config.lead')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('booking.close')}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#4a544c',
            padding: 4,
          }}
        >
          <X size={16} />
        </button>
      </div>
      <div style={{ padding: 20, overflowY: 'auto', flex: '1 1 auto' }} className="space-y-5">
        {/* Tabs.
              THE SELECTED TAB IS FILLED, not merely lighter. White-on-pale
              told the two apart only if you looked for it, and «which half of
              this dialog am I editing» is the one thing the header has to
              answer at a glance. Filled green is what every other selected
              control in here already uses, so it reads as selection rather
              than as decoration. `aria-selected` says the same to a screen
              reader, which nothing did before. */}
        <div
          role="tablist"
          className="flex gap-1 p-1 rounded-lg bg-bg-soft/50 border border-line -mt-1"
        >
          {(
            [
              ['general', t('booking.config.tab.general')],
              ['courts', t('booking.config.tab.courts')],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 text-xs font-semibold px-3 py-1.5 rounded transition-colors ${
                activeTab === id
                  ? 'bg-emerald-700 text-white shadow'
                  : 'text-ink-3 hover:text-ink-1 hover:bg-white/60'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'general' && (
          <>
            {/* Gap rule — smallest allowed gap between bookings */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span id={ids.gap} className="text-sm font-semibold text-ink-1">
                  {t('booking.config.gap.label')}
                </span>
                <span className="text-xs font-mono text-ink-3">
                  {draft.bufferMin === 0
                    ? t('booking.config.off')
                    : t('booking.config.minutes', { n: String(draft.bufferMin) })}
                </span>
              </div>
              <p className="text-[11px] text-ink-3 mb-2 leading-relaxed">
                {t('booking.config.gap.help')}
              </p>
              <OptionPicker
                labelledBy={ids.gap}
                builtIn={GAP_BUILT_IN}
                custom={draft.customGapOptions}
                mode="single"
                selected={draft.bufferMin}
                onSelect={(v) => setDraft((s) => ({ ...s, bufferMin: v }))}
                onAddCustom={(custom) => setDraft((s) => ({ ...s, customGapOptions: custom }))}
                onRemoveCustom={(v) =>
                  setDraft((s) => ({
                    ...s,
                    customGapOptions: removeCustomOption(s.customGapOptions, v),
                    // Deleting the chip that IS the current rule has to move
                    // the rule too, visibly, rather than leave a number with
                    // nothing on screen to explain it.
                    bufferMin: valueAfterRemoval(s.bufferMin, v, 60),
                  }))
                }
                labelFor={(v) =>
                  v === 0
                    ? t('booking.config.gap.none')
                    : t('booking.config.minutes', { n: String(v) })
                }
                bounds={GAP_BOUNDS}
                unit="minutes"
              />
            </div>

            <div>
              {/* A CHECKBOX AND ITS TEXT IN ONE LABEL, ONE LEVEL DEEP (#900). The
                  text sat in a <div> in a <div> in the label: block elements a
                  label may not hold, and deeper than its name is looked for. A
                  two-column grid of spans draws the same box. */}
              <label className="grid grid-cols-[auto_1fr] gap-x-3 items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.smartAllocation}
                  onChange={(e) => setDraft((s) => ({ ...s, smartAllocation: e.target.checked }))}
                  className="mt-0.5 w-5 h-5 accent-emerald-600 flex-shrink-0"
                />
                <span className="block text-sm font-semibold text-ink-1">
                  {t('booking.config.smart.label')}{' '}
                  <span className="font-normal text-ink-3">{t('booking.config.smart.sub')}</span>
                </span>
                <span className="col-start-2 block text-[11px] text-ink-3 mt-0.5 leading-relaxed">
                  {t('booking.config.smart.help')}
                </span>
              </label>
            </div>

            {/* Direct booking from the calendar */}
            <div>
              <label className="grid grid-cols-[auto_1fr] gap-x-3 items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.allowDirectBooking}
                  onChange={(e) =>
                    setDraft((s) => ({ ...s, allowDirectBooking: e.target.checked }))
                  }
                  className="mt-0.5 w-5 h-5 accent-emerald-600 flex-shrink-0"
                />
                <span className="block text-sm font-semibold text-ink-1">
                  {t('booking.config.direct.label')}
                </span>
                <span className="col-start-2 block text-[11px] text-ink-3 mt-0.5 leading-relaxed">
                  {t('booking.config.direct.help')}
                </span>
              </label>
            </div>

            {/* Allowed durations */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span id={ids.durations} className="text-sm font-semibold text-ink-1">
                  {t('booking.config.durations.label')}
                </span>
                <span className="text-xs font-mono text-ink-3">
                  {t('booking.config.durations.count', {
                    n: String(draft.allowedDurations.length),
                  })}
                </span>
              </div>
              <p className="text-[11px] text-ink-3 mb-2">{t('booking.config.durations.help')}</p>
              <OptionPicker
                labelledBy={ids.durations}
                builtIn={DURATION_BUILT_IN}
                custom={draft.customDurationOptions}
                mode="multi"
                selected={draft.allowedDurations}
                onSelect={toggleDuration}
                onAddCustom={(custom) => setDraft((s) => ({ ...s, customDurationOptions: custom }))}
                onRemoveCustom={(v) =>
                  setDraft((s) => ({
                    ...s,
                    customDurationOptions: removeCustomOption(s.customDurationOptions, v),
                    // Deselect it as well. A duration left in allowedDurations
                    // with no chip is still offered to customers while the
                    // dialog shows no sign of it.
                    allowedDurations: selectionAfterRemoval(s.allowedDurations, v),
                  }))
                }
                labelFor={(d) => t('booking.config.minutes', { n: String(d) })}
                bounds={DURATION_BOUNDS}
                unit="minutes"
              />
              {draft.allowedDurations.length === 0 && (
                <div className="mt-2 text-[11px] text-rose font-semibold">
                  {t('booking.config.durations.required')}
                </div>
              )}
            </div>

            {/* Opening hours */}
            <div>
              <div id={ids.hours} className="text-sm font-semibold text-ink-1 block mb-1">
                {t('booking.config.hours.label')}
              </div>
              <p className="text-[11px] text-ink-3 mb-2">{t('booking.config.hours.help')}</p>
              {hoursUnsaved && (
                <p
                  role="status"
                  className="text-[12px] mb-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2"
                >
                  {t('booking.config.hours.unsaved')}
                </p>
              )}
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={draft.openTime === '00:00' && draft.closeTime === '00:00'}
                  onChange={(e) => {
                    if (e.target.checked)
                      setDraft((s) => ({ ...s, openTime: '00:00', closeTime: '00:00' }));
                    else setDraft((s) => ({ ...s, openTime: '07:00', closeTime: '23:00' }));
                  }}
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-xs font-semibold text-ink-2">
                  {t('booking.config.hours.allDay')}
                </span>
              </label>
              {!(draft.openTime === '00:00' && draft.closeTime === '00:00') && (
                <>
                  <div className="grid grid-cols-2 gap-3" role="group" aria-labelledby={ids.hours}>
                    <div>
                      <label
                        htmlFor={ids.openTime}
                        className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                      >
                        {t('booking.config.hours.open')}
                      </label>
                      <input
                        id={ids.openTime}
                        type="time"
                        step="1800"
                        className="input w-full"
                        value={draft.openTime}
                        onChange={(e) => setDraft((s) => ({ ...s, openTime: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={ids.closeTime}
                        className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                      >
                        {t('booking.config.hours.close')}
                      </label>
                      <input
                        id={ids.closeTime}
                        type="time"
                        step="1800"
                        className="input w-full"
                        value={draft.closeTime}
                        onChange={(e) => setDraft((s) => ({ ...s, closeTime: e.target.value }))}
                      />
                    </div>
                  </div>
                  {toMinHHMM(draft.closeTime) <= toMinHHMM(draft.openTime) && (
                    <p className="text-[11px] text-emerald-700 mt-1.5 font-medium">
                      {t('booking.config.hours.overMidnight', {
                        close: draft.closeTime,
                        hours: String(
                          Math.round(
                            (1440 - toMinHHMM(draft.openTime) + toMinHHMM(draft.closeTime)) / 60,
                          ),
                        ),
                      })}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Time range for the availability color indicator */}
            <div>
              <div id={ids.availability} className="text-sm font-semibold text-ink-1 block mb-1">
                {t('booking.config.availability.label')}
              </div>
              <p className="text-[11px] text-ink-3 mb-2 leading-relaxed">
                {t('booking.config.availability.help')}
              </p>
              <div
                className="grid grid-cols-2 gap-3"
                role="group"
                aria-labelledby={ids.availability}
              >
                <div>
                  <label
                    htmlFor={ids.availabilityFrom}
                    className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                  >
                    {t('booking.config.availability.from')}
                  </label>
                  <input
                    id={ids.availabilityFrom}
                    type="time"
                    step="1800"
                    className="input w-full"
                    value={draft.availabilityFromTime || ''}
                    placeholder={draft.openTime}
                    onChange={(e) =>
                      setDraft((s) => ({ ...s, availabilityFromTime: e.target.value || null }))
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor={ids.availabilityTo}
                    className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                  >
                    {t('booking.config.availability.to')}
                  </label>
                  <input
                    id={ids.availabilityTo}
                    type="time"
                    step="1800"
                    className="input w-full"
                    value={draft.availabilityToTime || ''}
                    placeholder={draft.closeTime}
                    onChange={(e) =>
                      setDraft((s) => ({ ...s, availabilityToTime: e.target.value || null }))
                    }
                  />
                </div>
              </div>
              {(draft.availabilityFromTime || draft.availabilityToTime) && (
                <button
                  onClick={() =>
                    setDraft((s) => ({
                      ...s,
                      availabilityFromTime: null,
                      availabilityToTime: null,
                    }))
                  }
                  className="text-[11px] text-ink-3 hover:text-ink-1 underline mt-1.5"
                >
                  {t('booking.config.availability.reset')}
                </button>
              )}
            </div>

            {/* Booking window — how many days ahead regular users can book */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span id={ids.window} className="text-sm font-semibold text-ink-1">
                  {t('booking.config.window.label')}
                </span>
                <span className="text-xs font-mono text-ink-3">
                  {t('booking.config.days', { n: String(draft.bookingWindowDays) })}
                </span>
              </div>
              <p className="text-[11px] text-ink-3 mb-2 leading-relaxed">
                {t('booking.config.window.help')}
              </p>
              <OptionPicker
                labelledBy={ids.window}
                builtIn={WINDOW_BUILT_IN}
                custom={draft.customWindowDayOptions}
                mode="single"
                selected={draft.bookingWindowDays}
                onSelect={(v) => setDraft((s) => ({ ...s, bookingWindowDays: v }))}
                onAddCustom={(custom) =>
                  setDraft((s) => ({ ...s, customWindowDayOptions: custom }))
                }
                onRemoveCustom={(v) =>
                  setDraft((s) => ({
                    ...s,
                    customWindowDayOptions: removeCustomOption(s.customWindowDayOptions, v),
                    bookingWindowDays: valueAfterRemoval(s.bookingWindowDays, v, 7),
                  }))
                }
                labelFor={(v) => t('booking.config.days', { n: String(v) })}
                bounds={WINDOW_DAY_BOUNDS}
                unit="days"
              />
            </div>

            {/* Cancellation deadline — how late a member may cancel their own
                booking in the app (#1135, owner decision 2026-11-01). */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span id={ids.cancellation} className="text-sm font-semibold text-ink-1">
                  {t('booking.config.cancellation.label')}
                </span>
              </div>
              <p className="text-[11px] text-ink-3 mb-2 leading-relaxed">
                {t('booking.config.cancellation.help')}
              </p>
              <div
                className="flex flex-wrap gap-1.5"
                role="group"
                aria-labelledby={ids.cancellation}
                data-testid="booking-config-cancellation"
              >
                {CANCELLATION_HOURS.map((h) => (
                  <button
                    key={h}
                    onClick={() => setDraft((s) => ({ ...s, cancellationDeadlineHours: h }))}
                    aria-pressed={draft.cancellationDeadlineHours === h}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${draft.cancellationDeadlineHours === h ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/40 text-ink-2 hover:bg-bg-soft/70 border border-line'}`}
                  >
                    {h === 0
                      ? t('booking.config.cancellation.untilStart')
                      : t('booking.config.cancellation.hours', { n: String(h) })}
                  </button>
                ))}
                {!CANCELLATION_HOURS.includes(draft.cancellationDeadlineHours) && (
                  // A value set some other way (an import, support) is shown as
                  // it is, never silently replaced by the nearest chip.
                  <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-700 text-white shadow">
                    {t('booking.config.cancellation.hours', {
                      n: String(draft.cancellationDeadlineHours),
                    })}
                  </span>
                )}
              </div>
            </div>

            {/* Holidays — shared set for price zones and recurring bookings */}
            <div>
              <div id={ids.holidays} className="text-sm font-semibold text-ink-1 block mb-1">
                {t('booking.config.holidays.label')}
              </div>
              <p className="text-[11px] text-ink-3 mb-2 leading-relaxed">
                {t('booking.config.holidays.help')}
              </p>
              <div className="flex gap-1.5 mb-2" role="group" aria-labelledby={ids.holidays}>
                {[
                  ['no', t('booking.config.holidays.no')],
                  ['se', t('booking.config.holidays.se')],
                  ['dk', t('booking.config.holidays.dk')],
                ].map(([v, lbl]) => (
                  <button
                    key={v}
                    onClick={() => setDraft((s) => ({ ...s, holidayCountry: v }))}
                    aria-pressed={draft.holidayCountry === v}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${draft.holidayCountry === v ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/40 text-ink-2 hover:bg-bg-soft/70 border border-line'}`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
              {(() => {
                // Effective set = base set for selected country + extra − removed
                const base = HOLIDAYS[draft.holidayCountry] || HOLIDAYS.no;
                const set = new Set(base);
                (draft.holidayExtra || []).forEach((d) => set.add(d));
                (draft.holidayRemoved || []).forEach((d) => set.delete(d));
                // Show upcoming days (from today), sorted
                // LOCAL today (DOMAIN_RULES §4, #939).
                const todayStr = localDateOf(Date.now());
                const upcoming = [...set]
                  .filter((d) => d >= todayStr)
                  .sort()
                  .slice(0, 30);
                const fmt = (iso) => {
                  const d = new Date(iso + 'T00:00:00');
                  const wd = weekdayShort[d.getDay()];
                  return `${wd} ${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
                };
                const removeDay = (iso) =>
                  setDraft((s) => {
                    // If it's in the base set: add to removed. If it's extra: remove from extra.
                    if ((s.holidayExtra || []).includes(iso)) {
                      return { ...s, holidayExtra: s.holidayExtra.filter((x) => x !== iso) };
                    }
                    return { ...s, holidayRemoved: [...(s.holidayRemoved || []), iso] };
                  });
                const [_addVal, _setAddVal] = [draft._addHoliday || '', null];
                return (
                  <>
                    <div className="space-y-1 max-h-48 overflow-y-auto mb-2 pr-1">
                      {upcoming.length === 0 && (
                        <div className="text-[11px] text-ink-3 italic">
                          {t('booking.config.holidays.empty')}
                        </div>
                      )}
                      {upcoming.map((iso) => {
                        const navn = holidayName(iso, draft.holidayCountry);
                        const isCustom = (draft.holidayExtra || []).includes(iso) && !navn;
                        return (
                          <div
                            key={iso}
                            className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-bg-soft/30 border border-line"
                          >
                            <span className="flex items-baseline gap-1.5 min-w-0">
                              <span className="text-ink-1 font-medium truncate">
                                {navn || (isCustom ? t('booking.config.holidays.custom') : '—')}
                              </span>
                              <span className="text-ink-3 flex-shrink-0">{fmt(iso)}</span>
                            </span>
                            <button
                              onClick={() => removeDay(iso)}
                              className="text-[10px] text-rose hover:underline flex-shrink-0 ml-2"
                            >
                              {t('booking.config.holidays.remove')}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    {/* Add a custom date */}
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        aria-labelledby={`${ids.holidays} ${ids.addHoliday}`}
                        value={draft._addHoliday || ''}
                        onChange={(e) => setDraft((s) => ({ ...s, _addHoliday: e.target.value }))}
                        className="input text-sm flex-1"
                      />
                      <button
                        onClick={() =>
                          setDraft((s) => {
                            const v = s._addHoliday;
                            if (!v) return s;
                            const extra = new Set(s.holidayExtra || []);
                            extra.add(v);
                            // If it was removed, drop it from removed
                            const removed = (s.holidayRemoved || []).filter((x) => x !== v);
                            return {
                              ...s,
                              holidayExtra: [...extra],
                              holidayRemoved: removed,
                              _addHoliday: '',
                            };
                          })
                        }
                        id={ids.addHoliday}
                        className="text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800"
                      >
                        {t('booking.config.holidays.add')}
                      </button>
                    </div>
                    {(draft.holidayExtra?.length > 0 || draft.holidayRemoved?.length > 0) && (
                      <button
                        onClick={() =>
                          setDraft((s) => ({ ...s, holidayExtra: [], holidayRemoved: [] }))
                        }
                        className="text-[11px] text-ink-3 hover:text-ink-1 underline mt-1.5"
                      >
                        {t('booking.config.holidays.reset')}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}

        {activeTab === 'courts' && (
          <>
            {/* Number of courts — can be set independently of the center's physical count */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <label htmlFor={ids.courtCount} className="text-sm font-semibold text-ink-1">
                  {t('booking.config.courts.count')}
                </label>
                <span className="text-xs font-mono text-ink-3">
                  {t('booking.config.courts.countValue', { n: String(draft.courtCount) })}
                </span>
              </div>
              <p className="text-[11px] text-ink-3 mb-2 leading-relaxed">
                {t('booking.config.courts.help', { n: String(totalCourts) })}
              </p>
              <select
                id={ids.courtCount}
                className="input w-full"
                value={draft.courtCount}
                onChange={(e) => setDraft((s) => ({ ...s, courtCount: Number(e.target.value) }))}
              >
                {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {t(
                      n === 1
                        ? 'booking.config.courts.option.one'
                        : 'booking.config.courts.option.other',
                      { n: String(n) },
                    )}
                  </option>
                ))}
              </select>
            </div>

            {/* Global default facilities for all courts */}
            <div>
              <div id={ids.facilities} className="text-sm font-semibold text-ink-1 block mb-1">
                {t('booking.config.facilities.label')}
              </div>
              <p className="text-[11px] text-ink-3 mb-2 leading-relaxed">
                {t('booking.config.facilities.help')}
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-ink-2 w-20 flex-shrink-0">
                    {t('booking.config.facilities.courtType')}
                  </span>
                  <div className="flex gap-1.5">
                    {[
                      ['double', t('booking.config.facilities.double')],
                      ['single', t('booking.config.facilities.single')],
                    ].map(([v, lbl]) => (
                      <button
                        key={v}
                        onClick={() =>
                          setDraft((s) => ({
                            ...s,
                            courtDefaults: { ...s.courtDefaults, courtType: v },
                          }))
                        }
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${draft.courtDefaults.courtType === v ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/40 text-ink-2 hover:bg-bg-soft/70 border border-line'}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!draft.courtDefaults.runoff}
                    onChange={(e) =>
                      setDraft((s) => ({
                        ...s,
                        courtDefaults: { ...s.courtDefaults, runoff: e.target.checked },
                      }))
                    }
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <span className="text-xs text-ink-2">
                    {t('booking.config.facilities.runoff')}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!draft.courtDefaults.streaming}
                    onChange={(e) =>
                      setDraft((s) => ({
                        ...s,
                        courtDefaults: { ...s.courtDefaults, streaming: e.target.checked },
                      }))
                    }
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <span className="text-xs text-ink-2">
                    {t('booking.config.facilities.streaming')}
                  </span>
                </label>
              </div>
            </div>

            {/* Per court: nickname + sponsor + facilities — hidden behind a toggle */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={customizeCourts}
                  onChange={(e) => setCustomizeCourts(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-sm font-semibold text-ink-1">
                  {t('booking.config.perCourt.label')}
                </span>
              </label>
              {customizeCourts && (
                <>
                  <p className="text-[11px] text-ink-3 mt-1.5 mb-2 leading-relaxed">
                    {t('booking.config.perCourt.help')}
                  </p>
                  <div className="space-y-2.5">
                    {Array.from({ length: draft.courtCount }, (_, i) => i + 1).map((c) => {
                      const meta = draft.courtMeta[c] || {};
                      const setMeta = (patch) =>
                        setDraft((s) => ({
                          ...s,
                          courtMeta: {
                            ...s.courtMeta,
                            [c]: { ...(s.courtMeta[c] || {}), ...patch },
                          },
                        }));
                      const hasOverride = ['courtType', 'runoff', 'streaming'].some(
                        (k) => meta[k] != null,
                      );
                      return (
                        <div
                          key={c}
                          className={`rounded-lg border p-2.5 ${hasOverride ? 'border-emerald-300 bg-emerald-50/30' : 'border-line bg-bg-soft/20'}`}
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 w-12 flex-shrink-0">
                              {t('booking.config.courtLabel', { n: String(c) })}
                            </span>
                            <input
                              type="text"
                              className="input flex-1 text-sm"
                              placeholder={t('booking.config.perCourt.nicknamePlaceholder', {
                                n: String(c),
                              })}
                              // The placeholder is a hint, gone once typed into (#1144).
                              aria-label={t('booking.config.perCourt.nicknameLabel', {
                                n: String(c),
                              })}
                              value={meta.nickname || ''}
                              onChange={(e) => setMeta({ nickname: e.target.value })}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              className="input flex-1 text-sm"
                              placeholder={t('booking.config.perCourt.sponsorPlaceholder')}
                              aria-label={t('booking.config.perCourt.sponsorLabel', {
                                n: String(c),
                              })}
                              value={meta.sponsorName || ''}
                              onChange={(e) => setMeta({ sponsorName: e.target.value })}
                            />
                            <input
                              type="text"
                              className="input flex-1 text-sm"
                              placeholder={t('booking.config.perCourt.logoPlaceholder')}
                              aria-label={t('booking.config.perCourt.logoLabel', { n: String(c) })}
                              value={
                                meta.sponsorLogo && !meta.sponsorLogo.startsWith('data:')
                                  ? meta.sponsorLogo
                                  : ''
                              }
                              onChange={(e) => setMeta({ sponsorLogo: e.target.value })}
                            />
                            <label className="text-[11px] font-semibold text-emerald-700 cursor-pointer whitespace-nowrap px-2 py-1 rounded border border-emerald-600 hover:bg-emerald-50">
                              {t('booking.config.perCourt.upload')}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onload = () => setMeta({ sponsorLogo: reader.result });
                                  reader.readAsDataURL(file);
                                }}
                              />
                            </label>
                          </div>
                          {meta.sponsorLogo && (
                            <div className="flex items-center gap-2 mt-1.5">
                              <img
                                src={meta.sponsorLogo}
                                alt=""
                                className="h-6 w-auto object-contain"
                              />
                              <button
                                onClick={() => setMeta({ sponsorLogo: '' })}
                                className="text-[10px] text-rose hover:underline"
                              >
                                {t('booking.config.perCourt.removeLogo')}
                              </button>
                            </div>
                          )}
                          {/* Per-court facility override. Each field has three
                            states: Default (inherit globally), or an explicit
                            value that overrides. "Default" = the field is undefined. */}
                          {(() => {
                            const def = draft.courtDefaults || {};
                            const overrides = ['courtType', 'runoff', 'streaming'].filter(
                              (k) => meta[k] != null,
                            ).length;
                            // Tri-state button row: value = current, options = [[val,label]...]
                            const TriRow = ({ label, field, options, defLabel }) => {
                              const cur = meta[field]; // undefined = default
                              return (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[11px] text-ink-2 w-16 flex-shrink-0">
                                    {label}
                                  </span>
                                  <button
                                    onClick={() => setMeta({ [field]: undefined })}
                                    className={`text-[11px] font-semibold px-2 py-0.5 rounded transition-colors ${cur == null ? 'bg-ink text-white' : 'bg-bg-soft/50 text-ink-3 hover:bg-bg-soft border border-line-strong'}`}
                                  >
                                    {t('booking.config.perCourt.defaultOption', {
                                      value: defLabel,
                                    })}
                                  </button>
                                  {options.map(([val, lbl]) => (
                                    <button
                                      key={String(val)}
                                      onClick={() => setMeta({ [field]: val })}
                                      className={`text-[11px] font-semibold px-2 py-0.5 rounded transition-colors ${cur === val ? 'bg-emerald-700 text-white' : 'bg-bg-soft/50 text-ink-3 hover:bg-bg-soft border border-line-strong'}`}
                                    >
                                      {lbl}
                                    </button>
                                  ))}
                                </div>
                              );
                            };
                            return (
                              <div
                                className={`mt-2 pt-2 border-t flex flex-col gap-1.5 ${overrides > 0 ? 'border-emerald-200' : 'border-line/60'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                                    {t('booking.config.perCourt.facilities')}
                                  </span>
                                  {overrides > 0 && (
                                    <button
                                      onClick={() =>
                                        setMeta({
                                          courtType: undefined,
                                          runoff: undefined,
                                          streaming: undefined,
                                        })
                                      }
                                      className="text-[10px] text-emerald-700 hover:underline font-semibold"
                                    >
                                      {t('booking.config.perCourt.resetOverrides', {
                                        n: String(overrides),
                                      })}
                                    </button>
                                  )}
                                </div>
                                <TriRow
                                  label={t('booking.config.perCourt.type')}
                                  field="courtType"
                                  defLabel={def.courtType === 'single' ? '1v1' : '2v2'}
                                  options={[
                                    ['double', t('booking.config.perCourt.doubleShort')],
                                    ['single', t('booking.config.perCourt.singleShort')],
                                  ]}
                                />
                                <TriRow
                                  label={t('booking.config.perCourt.runoff')}
                                  field="runoff"
                                  defLabel={t(
                                    def.runoff ? 'booking.config.on' : 'booking.config.off',
                                  )}
                                  options={[
                                    [true, t('booking.config.on')],
                                    [false, t('booking.config.off')],
                                  ]}
                                />
                                <TriRow
                                  label={t('booking.config.perCourt.streaming')}
                                  field="streaming"
                                  defLabel={t(
                                    def.streaming ? 'booking.config.on' : 'booking.config.off',
                                  )}
                                  options={[
                                    [true, t('booking.config.on')],
                                    [false, t('booking.config.off')],
                                  ]}
                                />
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Active courts */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span id={ids.activeCourts} className="text-sm font-semibold text-ink-1">
                  {t('booking.config.activeCourts.label')}
                </span>
                <span className="text-xs font-mono text-ink-3">
                  {draft.enabledCourts.length === 0
                    ? t('booking.config.activeCourts.all', { n: String(draft.courtCount) })
                    : t('booking.config.activeCourts.someOf', {
                        n: String(draft.enabledCourts.length),
                        total: String(draft.courtCount),
                      })}
                </span>
              </div>
              <p className="text-[11px] text-ink-3 mb-2">{t('booking.config.activeCourts.help')}</p>
              <div
                className="flex gap-1.5 flex-wrap"
                role="group"
                aria-labelledby={ids.activeCourts}
              >
                {Array.from({ length: draft.courtCount }, (_, i) => i + 1).map((c) => (
                  <button
                    key={c}
                    onClick={() => toggleCourt(c)}
                    aria-pressed={isCourtEnabled(c)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border-2 ${
                      isCourtEnabled(c)
                        ? 'bg-emerald-700 text-white border-emerald-800 shadow'
                        : 'bg-bg-soft/40 text-ink-3 border-line line-through'
                    }`}
                  >
                    {(draft.courtMeta[c]?.nickname || '').trim() ||
                      t('booking.config.courtLabel', { n: String(c) })}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Quick reset */}
        <div className="pt-3 border-t border-line">
          <button
            onClick={() =>
              setDraft({
                bufferMin: 60,
                allowedDurations: [60, 90, 120],
                openTime: '07:00',
                closeTime: '23:00',
                enabledCourts: [],
                stepMin: 30,
                smartAllocation: true,
                allowDirectBooking: true,
                availabilityFromTime: null,
                availabilityToTime: null,
                bookingWindowDays: 7,
                cancellationDeadlineHours: DEFAULT_CANCELLATION_DEADLINE_HOURS,
                // «Reset to defaults» means the product's own chips only —
                // the club's additions go with everything else it clears.
                customGapOptions: [],
                customDurationOptions: [],
                customWindowDayOptions: [],
                courtCount: totalCourts,
                courtMeta: {},
                courtDefaults: { courtType: 'double', runoff: false, streaming: false },
                holidayCountry: 'no',
                holidayExtra: [],
                holidayRemoved: [],
              })
            }
            className="text-xs text-ink-3 hover:text-ink-1 underline"
          >
            {t('booking.config.resetAll')}
          </button>
        </div>
      </div>
      <div
        style={{
          padding: '12px 20px',
          borderTop: '1px solid #e7eae3',
          background: '#fafbf7',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        <button onClick={onClose} className="btn-outline text-sm">
          {t('booking.cancel')}
        </button>
        <button
          onClick={() => {
            const { _addHoliday, ...clean } = draft;
            onSave(clean);
          }}
          // Dead until something has actually moved. An always-live Save on
          // a dialog of twenty settings invites a save that writes the
          // document back unchanged — harmless in itself, but it bumps the
          // version and loses a concurrent edit for no reason at all.
          disabled={draft.allowedDurations.length === 0 || !dirty}
          className={
            dirty
              ? 'btn-lime text-sm disabled:opacity-40'
              : 'btn-outline text-sm opacity-50 cursor-default'
          }
        >
          {t('booking.config.save')}
        </button>
      </div>
    </Dialog>
  );
}
